import PropTypes, { bool, number, oneOfType } from "prop-types";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";
import { themeSizes } from "theme";

import { motion } from "motion/react";

import FnrErrorText from "UI/FnrErrorText";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import { useIsfocused } from "Utilities/Hooks/useIsHoverFocused";

import AnimatedIcon from "./AnimatedIcon";
import Input from "./Input";
import Textarea from "./Textarea";
import { defaultSize } from "./constants";
import fnrInputTypes from "./fnrInputTypes";
import { useIcon, useIconColor } from "./hooks";
import { shiftRight } from "./interpolations";

const labelFullValues = {
  xs: { height: 48, size: 18, top: 0 },
  sm: { height: 48, size: 18, top: 0 },
  md: { height: 48, size: 18, top: 1 },
  lg: { height: 58, size: 20, top: 1.5 },
  xl: { height: 72, size: 28, top: 2 },
};

const FnrInput2 = forwardRef(
  /**
   *
   * @param {object} props
   * @param {boolean} props.multiline - set to true for the multiline UX
   * @param {string|number} props.value - the value forwarded to the input
   * @param {string} props.label - text for the label displayed to the user. Used as a placeholder when input is empty.
   * @param {string} props.placeholder - passed to native input. label is not used as placeholder when defined.
   * @param {string} props.icon - a string representing a font awesome icon. Pass in false to override defaults.
   * @param {string} props.backgroundColor - ??
   * @param {string} props.borderColor - either a hexcode or a color from the theme file
   * @param {string} props.iconColor - either a hexcode or a color from the theme file
   * @param {string} props.textColor - a hexcode for the entered text's and label's color
   * @param {boolean} props.fullWidth - forwards a flex-grow: 1 value to the container
   * @param {'xs'|'sm'|'md'|'lg'|'xl'} props.size - forwarded to inputs to adjust the size via padding
   * @param {function} props.onChange - forwarded to the input and called natively
   * @param {function} props.setValue - redundant to onChange and will be overridden by it
   * @param {number} props.charLimit - limit the number of chars the user can enter
   * @param {number} props.maxWidth - limits the width of the input
   * @param {number|boolean} props.autofocus - number in ms delays the autofocus
   * @param {boolean} props.stout - shorter and fatter with a thicker border
   * @param {import("react").Ref} forwardedRef
   * @returns
   */
  (
    {
      className, // for styling container with styled-components. Should not be passed down.
      autofocus: autofocusArg, // passed down explicitly after modification
      ...props
    },
    forwardedRef
  ) => {
    const {
      multiline,
      value,
      label,
      placeholder,
      icon: iconArg,
      backgroundColor = "#fff",
      iconColor: iconColorArg,
      textColor = "#333",
      fullWidth,
      maxWidth,
      type,
      size = defaultSize,
    } = props;
    const [errorText, setErrorText] = useState("");
    const icon = useIcon({ iconArg, type });
    const iconColor = useIconColor({ iconColorArg, type });

    const hasValue = value && value !== "";
    const hasIcon = icon && icon !== "";
    const hasPlaceHolder = useMemo(
      () =>
        placeholder ||
        type === fnrInputTypes.currency || // currency has the built in placeholder of $0.00.
        type === fnrInputTypes.email || // email has the built in placeholder of example@example.com.
        type === fnrInputTypes.taxid, // defaults to XX-XXXXXXX
      [placeholder, type]
    );

    const hasValueOrPlaceHolder = hasValue || hasPlaceHolder;

    const ref = useRef();

    const refToUse = forwardedRef || ref;

    const focusInput = () => refToUse.current.focus();

    const isFocused = useIsfocused(refToUse);

    const labelStyle = useMemo(
      () =>
        hasValueOrPlaceHolder
          ? {
              top: "-7px",
              height: "20px",
              opacity: 1,
            }
          : {
              display: "flex",
              alignItems: "center",
              height: `min(100%, ${labelFullValues[size].height}px)`,
              top: `${labelFullValues[size].top}px`,
              opacity: 1,
            },
      [hasValueOrPlaceHolder, size]
    );

    const labelTextStyle = {
      fontSize: hasValueOrPlaceHolder
        ? "14px"
        : `${labelFullValues[size].size}px`,
      opacity: hasValueOrPlaceHolder ? 0.8 : 0.4,
    };

    const autofocus = useMemo(() => {
      if (typeof autofocusArg === "boolean") return autofocusArg; // we are using the prop conventionally.
      return false;
    }, [autofocusArg]);

    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
      setIsMounted(true);
      return () => setIsMounted(false);
    }, []);
    useEffect(() => {
      if (typeof autofocusArg !== "number" || !isMounted) return;
      setTimeout(() => {
        if (refToUse.current && isMounted) {
          refToUse.current.focus();
        }
      }, autofocusArg);
    }, [autofocusArg, refToUse, isMounted]);

    return (
      <SFnrVerticalGap gap={0} $fullWidth={fullWidth} $maxWidth={maxWidth}>
        <Wrapper $fullWidth={fullWidth} className={className}>
          <AnimatedIcon size={size} icon={icon} iconColor={iconColor} />
          {label ? (
            <Label
              $hasIcon={hasIcon}
              initial={labelStyle}
              animate={labelStyle}
              transition={{ duration: 0.2, type: "spring" }}
              onClick={focusInput}
              data-cy="FnrInput_Label"
              $size={size}
            >
              <LabelText
                style={{ color: textColor }}
                initial={labelTextStyle}
                animate={labelTextStyle}
                transition={{ duration: 0.2 }}
              >
                {label}
              </LabelText>
              <LabelLineHider
                $multiline={multiline}
                $isFocused={isFocused}
                style={{ borderColor: backgroundColor }}
                animate={{
                  opacity: hasValueOrPlaceHolder ? 1 : 0,
                }}
                transition={{ duration: 0.05 }}
                data-cy="FnrInput_LabelLineHider"
              />
            </Label>
          ) : null}

          {multiline ? (
            <Textarea
              ref={refToUse}
              setErrorText={setErrorText}
              icon={icon}
              autoFocus={autofocus}
              {...props}
            />
          ) : (
            <Input
              ref={refToUse}
              setErrorText={setErrorText}
              icon={icon}
              autoFocus={autofocus}
              {...props}
            />
          )}
        </Wrapper>
        <FnrErrorText error={Boolean(errorText)}>{errorText}</FnrErrorText>
      </SFnrVerticalGap>
    );
  }
);

FnrInput2.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func,

  label: PropTypes.string,
  id: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.bool, //if false
    PropTypes.string, // font awesome icon classes, ex: "fas fa-check"
  ]),
  multiline: PropTypes.bool, // determines input vs textarea

  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  /** is passed down to Input and Textarea where it is processed as an interpolation */
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  iconColor: PropTypes.string,
  textColor: PropTypes.string,
  borderRadius: PropTypes.string,
  isDark: PropTypes.bool, // for controlling textarea maxHeight scrollbar styles
  /** Use this to add a flex-grow: 1 to the container. This is useful when you need the input to take up the remain width in a flexbox */
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  type: PropTypes.oneOf(Object.values(fnrInputTypes)),
  placeholder: PropTypes.string,
  charLimit: PropTypes.number,
  className: PropTypes.string,
  maxWidth: number,
  autofocus: oneOfType([number, bool]),
};

export default FnrInput2;

const SFnrVerticalGap = styled(FnrVerticalGap)`
  ${({ $fullWidth }) => ($fullWidth ? `flex-grow: 1;` : "")}
  ${({ $maxWidth }) => ($maxWidth ? `max-width: ${$maxWidth}px;` : "")}
`;

const Wrapper = styled.div`
  position: relative;
`;

const Label = styled(motion.div)`
  pointer-events: none;
  left: ${shiftRight};
  position: absolute;
`;

const LabelText = styled(motion.div)`
  position: relative;
  z-index: 10;
`;

const LabelLineHider = styled(motion.div)`
  position: absolute;
  top: ${({ $isFocused }) => {
    if ($isFocused) return "5px";
    return "7px";
  }};
  ${({ $isFocused }) => (!$isFocused ? "transition: top 0.2s;" : "")}
  border-bottom: 4px solid;
  width: 100%;
  z-index: 1;
  transform: scaleX(1.05);
`;

// getFontSize is based on the same values as the fontSize functon in ./Input.js.
const getFontSize = (size) => {
  switch (size) {
    case themeSizes.lg:
      return "20px";
    case themeSizes.xl:
      return "28px";
    case themeSizes.md: // also default
    case themeSizes.xs:
    case themeSizes.sm:
    default:
      return "18px";
  }
};

const getInputContainerSize = (size) => {
  switch (size) {
    case themeSizes.lg:
      return "60px";
    case themeSizes.xl:
      return "72px";
    case themeSizes.md: // also default
    case themeSizes.xs:
    case themeSizes.sm:
    default:
      return "48px";
  }
};
